const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface InputTextConfig {
  label: string;
  description?: string;
  placeholder?: string;
  value?: string;
  isRequired: boolean;
  isEmail: boolean;
  isPassword: boolean;
  minLength: number | null;
  maxLength: number | null;
  icon?: string; // phosphor icon name e.g. "ph-envelope"
  onChange?: (value: string) => void;
}

export class InputTextComponent {
  private _input!: HTMLInputElement;
  private _errorSpan!: HTMLSpanElement;
  private _passwordVisible = false;

  constructor(
    private readonly _container: HTMLElement,
    private readonly _config: InputTextConfig,
  ) {}

  public init(): void {
    this._render();
    this._bindEvents();
  }

  public getValue(): string {
    return this._input.value.trim();
  }

  public validate(): boolean {
    return this._validate();
  }

  public getError(): string | null {
    return this._getError();
  }

  public showError(): void {
    this._validate();
  }

  public focus(): void {
    this._input.focus();
  }

  public disabled(): void {
    this._input.disabled = true;
  }

  public enabled(): void {
    this._input.disabled = false;
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  private _render(): void {
    const { label, description, placeholder, isRequired, isPassword, isEmail, icon } = this._config;
    const type = isPassword ? "password" : isEmail ? "email" : "text";
    const labelText = `${label}${isRequired ? " *" : ""}`;
    const hasGroup = !!icon || isPassword;

    let inputHtml: string;
    if (hasGroup) {
      const iconHtml = icon
        ? `<i class="ph ${icon} sx-input-icon" aria-hidden="true"></i>`
        : "";
      const toggleHtml = isPassword
        ? `<button class="itc-toggle" type="button" tabindex="-1" title="Mostrar/ocultar">
             <i class="ph ph-eye"></i>
           </button>`
        : "";
      inputHtml = `
        <div class="sx-input-group">
          ${iconHtml}
          <input class="sx-input${isPassword ? " itc-has-toggle" : ""}" type="${type}" placeholder="${placeholder ?? ""}" />
          ${toggleHtml}
        </div>`;
    } else {
      inputHtml = `<input class="sx-input" type="${type}" placeholder="${placeholder ?? ""}" />`;
    }

    const hintHtml = description
      ? `<span class="sx-field-hint">${description}</span>`
      : "";

    this._container.innerHTML = `
      <div class="sx-field">
        <label class="sx-label">${labelText}</label>
        ${inputHtml}
        ${hintHtml}
        <span class="sx-field-error"></span>
      </div>
    `;

    this._input     = this._container.querySelector<HTMLInputElement>("input")!;
    this._errorSpan = this._container.querySelector<HTMLSpanElement>(".sx-field-error")!;

    if (this._config.value !== undefined) {
      this._input.value = this._config.value;
    }
  }

  // ── Events ──────────────────────────────────────────────────────────────────

  private _bindEvents(): void {
    this._input.addEventListener("input", () => {
      this._validate();
      this._config.onChange?.(this._input.value.trim());
    });

    if (this._config.isPassword) {
      const toggle = this._container.querySelector<HTMLButtonElement>(".itc-toggle")!;
      toggle.addEventListener("click", () => {
        this._passwordVisible = !this._passwordVisible;
        this._input.type = this._passwordVisible ? "text" : "password";
        toggle.querySelector("i")!.className = this._passwordVisible
          ? "ph ph-eye-slash"
          : "ph ph-eye";
      });
    }
  }

  // ── Validation ───────────────────────────────────────────────────────────────

  private _validate(): boolean {
    const error = this._getError();
    this._setError(error);
    return error === null;
  }

  private _getError(): string | null {
    const value = this._input.value.trim();
    const { isRequired, isEmail, minLength, maxLength, label } = this._config;

    if (isRequired && value.length === 0)                       return `Ingrese ${label}`;
    if (minLength !== null && value.length < minLength)         return `Mínimo ${minLength} caracteres`;
    if (maxLength !== null && value.length > maxLength)         return `Máximo ${maxLength} caracteres`;
    if (isEmail && value.length > 0 && !EMAIL_REGEX.test(value)) return "Ingrese un email válido";

    return null;
  }

  private _setError(msg: string | null): void {
    this._errorSpan.textContent = msg ?? "";
    this._input.classList.toggle("error", msg !== null);
  }
}
